/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/* eslint-disable theme-colors/no-literal-colors */

/**
 * Carbon20 Theme Overrides
 *
 * This file defines the color overrides to align Superset's theme
 * with the Carbon20 brand colors from the main frontend application.
 *
 * Color mappings are based on:
 * - Carbon20 brand colors from legacy-carbon20-frontend/styles/global.scss
 * - Superset's theme structure from @superset-ui-core
 *
 * The primary color family uses Carbon20's brand blue (#2267EA) instead
 * of Superset's default teal (#20A7C9).
 */

// Carbon20 Brand Colors (from legacy-carbon20-frontend/styles/global.scss)
export const carbon20Colors = {
  // Primary brand color
  brand: '#2267EA',
  brandDarker: '#103681',
  brandDark: '#063888',
  brandMedium: '#296AE8',
  brandMediumLight: '#4884F8',
  brandLight: '#6A9DFF',
  brandLightest: '#D2E0FD',

  // Interactive states
  hover: '#EFF3FA',
  active: '#D3E1FB',
  buttonHover: '#4E85EE',
} as const;

// Interpolated colors to complete the Superset color scale
const interpolatedColors = {
  // Darker shades (for dark1, dark2)
  dark1: '#1B52BB', // Between brand and brandDarker
  dark2: '#103681', // brandDarker

  // Lighter shades (for light1-5 scale)
  light1: '#4E85EE', // buttonHover - interactive hover
  light2: '#6A9DFF', // brandLight
  light3: '#D2E0FD', // brandLightest
  light4: '#E8F0FE', // Very light background
  light5: '#F4F8FF', // Lightest - subtle backgrounds
} as const;

/**
 * Carbon20 theme overrides for Superset
 *
 * This object is designed to be merged with Superset's default theme
 * using lodash merge: merge(supersetTheme, carbon20ThemeOverrides)
 */
export const carbon20ThemeOverrides = {
  colors: {
    primary: {
      base: carbon20Colors.brand,
      dark1: interpolatedColors.dark1,
      dark2: interpolatedColors.dark2,
      light1: interpolatedColors.light1,
      light2: interpolatedColors.light2,
      light3: interpolatedColors.light3,
      light4: interpolatedColors.light4,
      light5: interpolatedColors.light5,
    },
  },
};

export default carbon20ThemeOverrides;
